import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listManufacturingMaterialAliases,
  postMaterialCostEntry,
} from "@/lib/manufacturingClient";
import type { MfgMaterial, MfgMaterialAlias } from "@/lib/manufacturing/types";
import { assignReceiveMaterial } from "@/lib/manufacturing/receiveLineAdd";
import {
  convertReceiveQtyToCanonical,
  defaultReceiveQtyMode,
  formatReceiveConversionSourceLabel,
  resolveReceiveConversionFactor,
  resolveReceiveConversionSource,
  type ReceiveQtyMode,
} from "@/lib/manufacturing/receiveQtyConversion";

export type ReceiveLine = {
  id: string;
  materialId: string;
  qty: string;
  unitCost: string;
  notes: string;
  /** When material has distinct shipping_unit, which denomination the qty is in. */
  qtyMode: ReceiveQtyMode;
  /** Optional vendor alias override for conversion factor (UQ-2). */
  aliasId: string;
};

export function makeReceiveLine(): ReceiveLine {
  return {
    id: crypto.randomUUID(),
    materialId: "",
    qty: "",
    unitCost: "",
    notes: "",
    qtyMode: "canonical",
    aliasId: "",
  };
}

/**
 * Local state + submit pipeline for the Receive panel. Mode-local: lives
 * only as long as the user is on the Receive mode tab; switching away
 * resets the in-flight lines (matches every other bucket's mode-local
 * editor state).
 *
 * UQ-1/UQ-2: when receiving in shipping units, multiplies qty by
 * `pieces_per_shipping_unit` or selected alias `unit_conversion_factor`
 * before posting cost-entry so on-hand stays in canonical units.
 */
export function useManufacturingReceiveLines(opts: {
  onPosted: () => void;
  materials: readonly MfgMaterial[];
}) {
  const { onPosted, materials } = opts;
  const [lines, setLines] = useState<ReceiveLine[]>([makeReceiveLine()]);
  const [working, setWorking] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [aliasesByMaterial, setAliasesByMaterial] = useState<
    Record<string, MfgMaterialAlias[]>
  >({});

  const materialIdsNeedingAliases = useMemo(() => {
    const ids = new Set<string>();
    for (const l of lines) {
      if (l.materialId) ids.add(l.materialId);
    }
    return [...ids];
  }, [lines]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      for (const materialId of materialIdsNeedingAliases) {
        if (aliasesByMaterial[materialId]) continue;
        const r = await listManufacturingMaterialAliases({ material_id: materialId });
        if (cancelled) return;
        if (!r.ok) {
          setAliasesByMaterial((prev) => ({ ...prev, [materialId]: [] }));
          continue;
        }
        setAliasesByMaterial((prev) => ({
          ...prev,
          [materialId]: Array.isArray(r.data.aliases) ? r.data.aliases : [],
        }));
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // aliasesByMaterial intentionally omitted — we only fetch missing keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialIdsNeedingAliases.join("|")]);

  const addLine = useCallback(
    (materialId?: string) => {
      if (typeof materialId !== "string" || !materialId) {
        setLines((prev) => [...prev, makeReceiveLine()]);
        return;
      }
      const mat = materials.find((m) => m.id === materialId);
      setLines((prev) =>
        assignReceiveMaterial(
          prev,
          materialId,
          mat
            ? {
                unit: mat.unit,
                shipping_unit: mat.shipping_unit,
                pieces_per_shipping_unit: Number(mat.pieces_per_shipping_unit) || 1,
              }
            : undefined,
          makeReceiveLine,
        ),
      );
    },
    [materials],
  );
  const removeLine = useCallback(
    (id: string) => setLines((prev) => prev.filter((l) => l.id !== id)),
    []
  );
  const updateLine = useCallback(
    (id: string, patch: Partial<Omit<ReceiveLine, "id">>) =>
      setLines((prev) =>
        prev.map((l) => {
          if (l.id !== id) return l;
          const next = { ...l, ...patch };
          if (typeof patch.materialId === "string" && patch.materialId !== l.materialId) {
            const mat = materials.find((m) => m.id === patch.materialId);
            next.qtyMode = mat
              ? defaultReceiveQtyMode({
                  unit: mat.unit,
                  shipping_unit: mat.shipping_unit,
                  pieces_per_shipping_unit: Number(mat.pieces_per_shipping_unit) || 1,
                })
              : "canonical";
            next.aliasId = "";
          }
          return next;
        })
      ),
    [materials]
  );

  const resolveLineFactor = useCallback(
    (line: ReceiveLine) => {
      const mat = materials.find((m) => m.id === line.materialId);
      const aliases = aliasesByMaterial[line.materialId] ?? [];
      const alias = line.aliasId ? aliases.find((a) => a.id === line.aliasId) : undefined;
      const pieces = Number(mat?.pieces_per_shipping_unit) || 1;
      const aliasFactor =
        alias && Number(alias.unit_conversion_factor) > 0
          ? Number(alias.unit_conversion_factor)
          : null;
      const factor = resolveReceiveConversionFactor({
        piecesPerShippingUnit: pieces,
        aliasUnitConversionFactor: aliasFactor,
      });
      const source = resolveReceiveConversionSource({
        aliasUnitConversionFactor: aliasFactor,
        aliasVendorName: alias?.vendor_name,
      });
      return {
        factor,
        sourceLabel: formatReceiveConversionSourceLabel(source),
        alias,
        pieces,
      };
    },
    [aliasesByMaterial, materials]
  );

  const submit = useCallback(async () => {
    const valid = lines.filter(
      (l) => l.materialId && parseFloat(l.qty) > 0 && parseFloat(l.unitCost) >= 0
    );
    if (!valid.length) {
      setToast("Add at least one complete receive line");
      return;
    }
    setWorking(true);
    let errors = 0;
    for (const line of valid) {
      const { pieces, alias } = resolveLineFactor(line);
      const enteredQty = parseFloat(line.qty);
      const quantity = convertReceiveQtyToCanonical({
        enteredQty,
        mode: line.qtyMode,
        piecesPerShippingUnit: pieces,
        aliasUnitConversionFactor:
          alias && Number(alias.unit_conversion_factor) > 0
            ? Number(alias.unit_conversion_factor)
            : null,
      });
      if (!(quantity > 0)) {
        errors++;
        continue;
      }
      const r = await postMaterialCostEntry({
        material_id: line.materialId,
        quantity,
        unit_cost: parseFloat(line.unitCost),
        entry_type: "receive",
        notes: line.notes,
      });
      if (!r.ok) errors++;
    }
    setWorking(false);
    if (errors > 0) {
      setToast(`${errors} line(s) failed to post`);
    } else {
      setToast(`${valid.length} line(s) received`);
      setLines([makeReceiveLine()]);
      onPosted();
    }
  }, [aliasesByMaterial, lines, onPosted, resolveLineFactor]);

  const dismissToast = useCallback(() => setToast(null), []);

  return {
    lines,
    setLines,
    addLine,
    removeLine,
    updateLine,
    submit,
    working,
    toast,
    dismissToast,
    aliasesByMaterial,
    resolveLineFactor,
  };
}
